import dayjs from "dayjs";

const numero = (valor) => Number(valor || 0);

const numeroOuNulo = (valor) =>
  valor === null || valor === undefined ? null : Number(valor);

export const periodoSemiaberto = (intervalo) => {
  if (!Array.isArray(intervalo)) return { start: null, end: null };
  const [inicio, fim] = intervalo;
  if (!inicio || !fim) return { start: null, end: null };
  return {
    start: dayjs(inicio).format("YYYY-MM-DD"),
    end: dayjs(fim).add(1, "day").format("YYYY-MM-DD"),
  };
};

export const tipoDoModo = (revenueMode) =>
  revenueMode === "presencial" ? "presencial" : "total";

export const valorDoModo = (metricas, revenueMode) =>
  numeroOuNulo(
    revenueMode === "presencial" ? metricas?.bruto_vendas : metricas?.liquido
  );

export const percentualPresencial = (presencial, total) => {
  const numerador = numeroOuNulo(presencial?.totals?.bruto_vendas);
  const denominador = numeroOuNulo(total?.totals?.bruto_vendas);
  if (numerador === null || !denominador) return null;
  return (numerador / denominador) * 100;
};

export const faturamentoPorFilial = (revenue, revenueMode) => {
  const mapa = {};
  (revenue?.groups || []).forEach((grupo) => {
    if (grupo.key === null || grupo.key === undefined) return;
    mapa[grupo.key] = {
      faturamento: valorDoModo(grupo, revenueMode),
      ticketMedio: numeroOuNulo(grupo.ticket_medio),
    };
  });
  return mapa;
};

export const conversaoPorFilial = (conversion) => {
  const mapa = {};
  (conversion?.branches || []).forEach((filial) => {
    mapa[filial.branch_id] = {
      oportunidades: numero(filial.oportunidades),
      entradasClientes: numeroOuNulo(filial.entradas_clientes),
      entradasTotais: numeroOuNulo(filial.entradas_totais),
      entradasBrutas: numeroOuNulo(filial.entradas_brutas),
      conversao: numeroOuNulo(filial.conversao_pct),
    };
  });
  return mapa;
};

const inteiro = new Intl.NumberFormat("pt-BR");

export const detalheDasOportunidades = ({
  oportunidades,
  entradasClientes,
  entradasTotais,
  entradasBrutas,
}) => {
  const separaVendedor =
    entradasClientes !== null && entradasClientes !== undefined;
  const total = separaVendedor ? entradasTotais : entradasBrutas;
  if (total === null || total === undefined) return [];
  const clientes = separaVendedor ? entradasClientes : total;
  const linhas = [`${inteiro.format(total)} entradas no total`];
  if (separaVendedor) {
    linhas.push(`− ${inteiro.format(total - clientes)} de vendedores`);
  }
  linhas.push(
    `− ${inteiro.format(clientes - oportunidades)} de clientes que entraram juntos`,
    `= ${inteiro.format(oportunidades)} oportunidades`
  );
  if (!separaVendedor) linhas.push("Esta loja ainda não separa vendedores.");
  return linhas;
};

export const filiaisDoClienteAtivo = (resposta, filialAtivaId) => {
  const filiais = resposta?.branches || [];
  const ativa = filiais.find((filial) => filial.id === filialAtivaId);
  if (!ativa) return { cliente: null, filiais: [] };
  return {
    cliente: ativa.client_name ?? null,
    filiais: filiais
      .filter((filial) => filial.client_id === ativa.client_id)
      .map((filial) => ({ branch_id: filial.id, branch_name: filial.name }))
      .sort((a, b) => a.branch_name.localeCompare(b.branch_name, "pt-BR")),
  };
};

export const derivadasPorFilial = (derived) => {
  const mapa = {};
  (derived?.branches || []).forEach((filial) => {
    mapa[filial.branch_id] = {
      faturamentoPorM2: numeroOuNulo(filial.faturamento_por_m2_mes),
      atencaoMedia: numeroOuNulo(filial.atencao_media_por_visitante),
      faturamentoPorAtencao: numeroOuNulo(filial.faturamento_por_pessoa_minuto),
    };
  });
  return mapa;
};

export const percentualDaMetaPorFilial = (goals) => {
  const mapa = {};
  (goals?.branches || []).forEach((filial) => {
    mapa[filial.branch_id] = filial.has_goals
      ? numeroOuNulo(filial.percent_ate_ultima_venda)
      : null;
  });
  return mapa;
};

export const serieDeMetaV2 = (goals) => {
  const porData = {};
  const ordem = [];
  (goals?.branches || []).forEach((filial) => {
    (filial.dias || []).forEach((dia) => {
      if (!porData[dia.date]) {
        porData[dia.date] = {
          date: dia.date,
          label: dayjs(dia.date).format("DD/MM"),
          actual_revenue: 0,
          target_revenue: 0,
        };
        ordem.push(dia.date);
      }
      porData[dia.date].actual_revenue += numero(dia.realizado);
      porData[dia.date].target_revenue += numero(dia.meta);
    });
  });
  const days = ordem.sort().map((data) => ({
    ...porData[data],
    actual_revenue: Math.round(porData[data].actual_revenue * 100) / 100,
    target_revenue: Math.round(porData[data].target_revenue * 100) / 100,
  }));
  const totais = goals?.totals || {};
  return {
    days,
    hasGoals: totais.meta_periodo !== null && totais.meta_periodo !== undefined,
    metaPeriodo: numero(totais.meta_periodo),
    realizadoPeriodo: numero(totais.realizado_periodo),
    ultimaVenda: totais.ultima_venda ?? null,
    realizadoAteUltimaVenda: numero(totais.realizado_ate_ultima_venda),
    percentAteUltimaVenda: numeroOuNulo(totais.percent_ate_ultima_venda),
  };
};

export const corteAteUltimaVenda = (days, hojeCompleto, hoje) => {
  let indice = -1;
  days.forEach((dia, i) => {
    const hojeIncompleto = dia?.date === hoje && !hojeCompleto;
    if (numero(dia?.actual_revenue) > 0 && !hojeIncompleto) indice = i;
  });
  const recorte = indice >= 0 ? days.slice(0, indice + 1) : [];
  const realizado = recorte.reduce(
    (acc, dia) => acc + numero(dia?.actual_revenue),
    0
  );
  const meta = recorte.reduce(
    (acc, dia) => acc + numero(dia?.target_revenue),
    0
  );
  return {
    ultimaVenda: indice >= 0 ? days[indice].date : null,
    realizadoAteUltimaVenda: realizado,
    percentAteUltimaVenda: meta > 0 ? (realizado / meta) * 100 : null,
  };
};

export const serieDeMetaLegado = (chart, hoje) => {
  const days = Array.isArray(chart?.days) ? chart.days : [];
  return {
    days,
    hasGoals: Boolean(chart?.has_goals),
    metaPeriodo: days.reduce(
      (acc, dia) => acc + numero(dia?.target_revenue),
      0
    ),
    realizadoPeriodo: days.reduce(
      (acc, dia) => acc + numero(dia?.actual_revenue),
      0
    ),
    ...corteAteUltimaVenda(
      days,
      chart?.today_complete_for_scope !== false,
      hoje
    ),
  };
};
